import { useEffect } from "react";

/* Tawk.to live-chat widget. Deferred-injection strategy for perf:
 *
 * Tawk's bundle is ~100 KB and blocks the main thread for ~400 ms
 * on first paint, which destroys Lighthouse LCP/TBT scores. We hold
 * the snippet back until ONE of:
 *
 *   • the page has been idle for 5 s
 *   • the user shows engagement (scroll, mousemove, touchstart, keydown, click)
 *
 * Whichever comes first. Either signal proves the user is past the
 * critical-render window and Tawk can boot without impacting metrics.
 * Layout-shift caused by Tawk's iframe stamping in at random size is
 * eliminated because by then everything else has rendered, so CLS no
 * longer counts toward Core Web Vitals. */
const TAWK_SRC =
  "https://embed.tawk.to/66a88b2632dca6db2cb7529c/1i415v482";

const ENGAGEMENT_EVENTS = ["scroll", "mousemove", "touchstart", "keydown", "click"];
const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

/* Tawk.to free plan offers no server webhooks · the only way to capture
 * visitor email/name into Brevo is from the JS API callbacks that fire
 * in the visitor's browser. We forward them to the backend, which then
 * upserts the contact into Brevo and fires a GA4 generate_lead event. */
function postVisitor(event, fields = {}) {
  if (!BACKEND) return;
  const body = {
    event,
    page_url: typeof window !== "undefined" ? window.location.href : "",
    ...fields,
  };
  // fire-and-forget · use keepalive so the request survives page unload
  // during onChatEnded right before the visitor closes the tab.
  try {
    fetch(`${BACKEND}/api/tawkto/visitor`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      keepalive: true,
    }).catch(() => {});
  } catch (_) {
    /* swallow · never block the chat UX on analytics */
  }
}

/* Tawk's onPrechatSubmit hands us an array of {label, answer} objects.
 * Default labels are "Name" and "Email" but admins can rename or add
 * fields, so we walk the array case-insensitively and pluck what we
 * can. Anything we don't recognise gets stuffed into `extras` as JSON. */
function parsePrechat(data) {
  const out = { name: "", email: "", phone: "", message: "", extras: {} };
  const arr = Array.isArray(data) ? data : (data?.questions || []);
  arr.forEach((q) => {
    const label = String(q?.label || q?.name || "").toLowerCase().trim();
    const ans   = String(q?.answer || q?.value || "").trim();
    if (!ans) return;
    if (label.includes("email"))         out.email = ans;
    else if (label.includes("phone"))    out.phone = ans;
    else if (label.includes("name"))     out.name  = ans;
    else if (label.includes("message"))  out.message = ans;
    else out.extras[label] = ans;
  });
  // Some Tawk configs pass {name, email, message} as plain object.
  if (!out.email && data?.email)   out.email   = String(data.email);
  if (!out.name  && data?.name)    out.name    = String(data.name);
  if (!out.phone && data?.phone)   out.phone   = String(data.phone);
  if (!out.message && data?.message) out.message = String(data.message);
  return out;
}

export default function TawkTo() {
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.__tawkInjected) return;

    let booted = false;
    const boot = () => {
      if (booted || window.__tawkInjected) return;
      booted = true;
      window.__tawkInjected = true;

      window.Tawk_API = window.Tawk_API || {};
      window.Tawk_LoadStart = new Date();

      // The TawkTo bubble sits flush with the viewport bottom on desktop,
      // and 20 px above the mobile bottom nav on mobile devices.
      window.Tawk_API.customStyle = {
        visibility: {
          desktop: { position: "br", xOffset: 20, yOffset: 20 },
          mobile:  { position: "br", xOffset: 12, yOffset: 85 },
        },
      };

      /* Visitor-capture callbacks · pipe Tawk events to backend → Brevo + GA4 */
      window.Tawk_API.onPrechatSubmit = (data) => {
        const f = parsePrechat(data);
        postVisitor("prechat_submit", f);
      };
      window.Tawk_API.onOfflineSubmit = (data) => {
        const f = parsePrechat(data);
        postVisitor("offline_form_submit", f);
      };
      window.Tawk_API.onChatStarted = () => {
        // Visitor object may carry a name/email if set via setAttributes.
        const v = (window.Tawk_API.visitor) || {};
        postVisitor("chat_started", { name: v.name || "", email: v.email || "" });
      };
      window.Tawk_API.onChatEnded = () => {
        const v = (window.Tawk_API.visitor) || {};
        postVisitor("chat_ended", { name: v.name || "", email: v.email || "" });
      };

      // Scale the collapsed launcher; expanded chat stays at 100%.
      const css = document.createElement("style");
      css.setAttribute("data-tawk-scale", "");
      css.textContent = `
        iframe[title="chat widget"] {
          transform: scale(0.7) !important;
          transform-origin: bottom right !important;
        }
      `;
      document.head.appendChild(css);

      const s = document.createElement("script");
      s.async = true;
      s.src = TAWK_SRC;
      s.charset = "UTF-8";
      document.body.appendChild(s);

      // Tawk's bootstrap inserts iframes without a `title` attribute,
      // which fails Lighthouse a11y (frame-title). Watch for them and
      // patch titles in once they mount · fires roughly once during
      // the whole session.
      const titleMap = {
        "chat widget": "Open live chat",
        "chat now": "Live chat",
      };
      const obs = new MutationObserver(() => {
        document.querySelectorAll("iframe").forEach((f) => {
          if (f.hasAttribute("title") && f.getAttribute("title")) return;
          const t =
            titleMap[f.getAttribute("title") || ""] ||
            (f.src.includes("tawk") ? "Live chat widget" : "");
          if (t) f.setAttribute("title", t);
        });
      });
      obs.observe(document.body, { childList: true, subtree: true });
    };

    // 5 s idle fallback in case the visitor never interacts.
    const idleTimer = setTimeout(boot, 5000);
    const onEvent = () => boot();
    ENGAGEMENT_EVENTS.forEach((ev) =>
      window.addEventListener(ev, onEvent, { once: true, passive: true }),
    );

    return () => {
      clearTimeout(idleTimer);
      ENGAGEMENT_EVENTS.forEach((ev) =>
        window.removeEventListener(ev, onEvent),
      );
    };
  }, []);

  return null;
}
